import type { Vec3 } from './assemblyPose';
import { bookOf } from './assemblyStore';
import type { StepBand } from './categoryBands';
import { MM_PER_TRUE_UNIT } from './meshAnnotations';
import { builtBoundsOf } from './openaiBlenderApi';
import type { PartItem } from './partsRun';
import { RuleError } from './ruleError';
import { pieceIdByKey, pieceItemId, type SubStep } from './stepsRun';
import { jobBounds, meshStamp, pieceOf, type Job3D } from './threeDApi';

// WHERE A PART OR A PIECE HAS ITS MIDDLE, IN THE PRODUCT'S FRAME — what a placing rule places ON.
//
// A part: where the product built it. A piece: it was signed off in the frame of the sub-step
// that made it, the part it was done onto standing in the middle — so it is that part's place,
// and the piece's own middle from there. The middle is the middle of its BOX, which is what the
// scene centres it on, asked of the server: the piece is a list of its parts, and downloading
// them put together to measure a box was 28 MB for six numbers.
//
// One function for every rule that places on a piece (see fromHole, fromJoint): two of them
// reading a piece's place two ways is two rules disagreeing about where a frame stands.

const centre = (b: { min: number[]; max: number[] }): Vec3 =>
  [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];

export function pieceMiddles(of: {
  docId: string;
  steps: StepBand[];
  subSteps: Record<string, SubStep[]>;
  items: PartItem[];
  meshOf: (id: string) => Job3D | null;
  // A part or piece by its id, as the page names it — what a RuleError says (see partLabel).
  label: (id: string) => string;
  // THE SUB-STEP WHOSE FITTING SPEAKS FOR ONE: itself, or the one it repeats. A repeat has no
  // sign-off of its own (see Slot.repeat), so the right rail's first nut, made at 1-9, read as a
  // piece nobody had signed off while 1-2, which it repeats, was.
  leaderOf: (key: string) => string;
}): (id: string, modelOf: Map<number, Job3D>) => Promise<Vec3> {
  const { docId, steps, subSteps, items, meshOf, label, leaderOf } = of;
  const makers = new Map([...pieceIdByKey(steps, subSteps)].map(([key, { id }]) => [id, key]));
  const jobMiddle = (job: Job3D | null | undefined, what: { key: string; id: string }): Vec3 => {
    const box = builtBoundsOf(job);
    if (!box || pieceOf(job)?.from !== 'product') throw new RuleError(what.key, { id: label(what.id) });
    return centre(box);
  };
  const middleOf = async (id: string, modelOf: Map<number, Job3D>): Promise<Vec3> => {
    if (items[Number(id)]) return jobMiddle(meshOf(id), { key: 'notice.rule.partNotCut', id });
    const maker = makers.get(id);
    const fitting = maker ? bookOf(docId).bySlot[leaderOf(maker)] : undefined;
    const job = modelOf.get(pieceItemId(id) ?? 0);
    if (!fitting?.validated || fitting.targetId == null || !job) {
      throw new RuleError('notice.rule.noPieceUnder', { id: label(id) });
    }
    if (fitting.targetSpin.some((d) => d !== 0)) throw new RuleError('notice.rule.madeOnTurned', { id: label(id) });
    const onto = fitting.targetId < 0
      ? await middleOf(String(-fitting.targetId), modelOf)
      : jobMiddle(modelOf.get(fitting.targetId), { key: 'notice.rule.madeOnNotCut', id });
    const box = await jobBounds(job.id, meshStamp(job));
    if (!box) throw new RuleError('notice.rule.modelOfPiece', { id: label(id), job: job.id });
    const c = centre(box).map((v) => v * MM_PER_TRUE_UNIT);
    return [onto[0] - fitting.targetSeat[0] + c[0], onto[1] - fitting.targetSeat[1] + c[1], onto[2] - fitting.targetSeat[2] + c[2]];
  };
  return middleOf;
}
